import FilterPopoverTabs from '@/Enums/ReportBuilder/FilterPopoverTabs.enum';
import type { IClientControlOption, IControlOption, IGroupedControlOption } from '@/Interfaces/ControlOption.interface';
import type { IFilterFormData } from '@/Interfaces/ReportBuilder/FilterFormData.interface';
import AddFilterButton from '@/components/Filter/AddFilterButton';
import FilterGroupChip from '@/components/Filter/FilterGroupChip';
import {
  AdvertisersTabButton,
  CampaignsTabButton,
  ClientsTabButton,
  PixelsTabButton,
} from '@/components/Filter/TabButtons';
import TabFilterPopover from '@/components/Filter/TabFilterPopover';
import TabPanel from '@/components/TabPanel';
import TabSelect from '@/components/TabSelect';
import { BASE_TAB_ID } from '@/constants/reportBuilder';
import { areArraysOfObjectsEqual } from '@/helpers/comparators';
import type { FilterSourceLoader } from '@/types/ReportBuilder/FilterPopover.types';
import { a11yProps } from '@/utils/tabProps';
import { Tab, Tabs, tabClasses } from '@mui/material';
import { type MouseEventHandler, type SyntheticEvent, useCallback, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';

interface IProps {
  filters: Partial<IFilterFormData>;
  clients: IClientControlOption[];
  campaigns: (IControlOption | IGroupedControlOption)[];
  events?: IControlOption[];
  loadingClients?: boolean;
  loadingCampaigns?: boolean;
  loadingEvents?: boolean;
  loadCampaigns?: FilterSourceLoader;
  loadEvents?: FilterSourceLoader;
  disabled?: boolean;
  groupedCampaigns?: boolean;
  showEventFilter?: boolean;
}

const FilterPopover = ({
  filters,
  clients,
  campaigns,
  events,
  loadingClients,
  loadingCampaigns,
  loadingEvents,
  loadCampaigns,
  loadEvents,
  disabled,
  groupedCampaigns,
  showEventFilter,
}: IProps) => {
  const { setValue } = useFormContext();

  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [currentTab, setCurrentTab] = useState(FilterPopoverTabs.Clients);

  const [pendingClients, setPendingClients] = useState<IClientControlOption[]>(filters.clients ?? []);
  const [pendingAdvertisers, setPendingAdvertisers] = useState<IControlOption[]>(filters.advertisers ?? []);
  const [pendingCampaigns, setPendingCampaigns] = useState<IControlOption[]>(filters.campaigns ?? []);
  const [pendingEvents, setPendingEvents] = useState<IControlOption[]>(filters.events ?? []);

  const resetPendingValues = useCallback(() => {
    setPendingClients(filters.clients ?? []);
    setPendingAdvertisers(filters.advertisers ?? []);
    setPendingCampaigns(filters.campaigns ?? []);
    setPendingEvents(filters.events ?? []);
  }, [filters.advertisers, filters.campaigns, filters.clients, filters.events]);

  const handleTabChange = (_event: SyntheticEvent, newValue: number): void => {
    setCurrentTab(newValue);
    if (newValue === FilterPopoverTabs.Campaigns) {
      loadCampaigns?.(pendingClients, pendingAdvertisers);
    }
    if (newValue === FilterPopoverTabs.Events) {
      loadEvents?.(pendingClients, pendingAdvertisers);
    }
  };

  const handleOpen = useCallback<MouseEventHandler<HTMLButtonElement>>(
    event => {
      resetPendingValues();
      setFilterAnchorEl(event.currentTarget);
    },
    [resetPendingValues],
  );

  const handleClose = useCallback(() => {
    setFilterAnchorEl(null);
    resetPendingValues();
    setCurrentTab(FilterPopoverTabs.Clients);
  }, [resetPendingValues]);

  const handleSubmit = () => {
    if (
      areArraysOfObjectsEqual(filters.clients, pendingClients) &&
      areArraysOfObjectsEqual(filters.advertisers, pendingAdvertisers) &&
      areArraysOfObjectsEqual(filters.campaigns, pendingCampaigns) &&
      areArraysOfObjectsEqual(filters.events, pendingEvents)
    ) {
      handleClose();
      return;
    }
    setValue('clients', pendingClients);
    setValue('advertisers', pendingAdvertisers);
    setValue('campaigns', pendingCampaigns);
    setValue('events', pendingEvents);
    handleClose();
  };

  const updateClientsValue = useCallback((updatedClients: IClientControlOption[] = []) => {
    setPendingClients(updatedClients);
    setPendingAdvertisers([]);
    setPendingCampaigns([]);
    setPendingEvents([]);
  }, []);

  const updateAdvertisersValue = useCallback((updatedAdvertisers: IControlOption[] = []) => {
    setPendingAdvertisers(updatedAdvertisers);
    setPendingCampaigns([]);
    setPendingEvents([]);
  }, []);

  const updateCampaignsValue = useCallback((updatedCampaigns: IControlOption[] = []) => {
    setPendingCampaigns(updatedCampaigns);
  }, []);

  const updateEventsValue = useCallback((updatedEvents: IControlOption[] = []) => {
    setPendingEvents(updatedEvents);
  }, []);

  const filtersOpen = useMemo(() => Boolean(filterAnchorEl), [filterAnchorEl]);
  const filtersId = useMemo(() => (filtersOpen ? 'filters-popover' : undefined), [filtersOpen]);

  const commonTabPanelProps = useMemo(
    () => ({
      className: 'w-full',
      baseTabId: BASE_TAB_ID,
      value: currentTab,
    }),
    [currentTab],
  );

  const advertisers = useMemo(() => {
    return (pendingClients || [])
      .map(client => client.advertisers)
      .flat()
      .filter(rec => !!rec)
      .sort((a, b) => a!.label?.localeCompare?.(b!.label));
  }, [pendingClients]);

  return (
    <>
      <AddFilterButton onClick={handleOpen} describedBy={filtersId} sx={{ marginRight: '6px' }} disabled={disabled} />
      <TabFilterPopover
        id={filtersId}
        open={filtersOpen}
        anchorEl={filterAnchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Tabs
          value={currentTab}
          orientation='vertical'
          onChange={handleTabChange}
          aria-label='report builder filter tabs'
          sx={{
            minHeight: 'unset',
            background: '#1B43320A',
            padding: '16px',
            minWidth: '230px',
            [`& .${tabClasses.root}`]: {
              paddingX: 0,
              minHeight: 'unset',
              justifyContent: 'flex-start',
              paddingLeft: '16px',
              paddingRight: '16px',
            },
            [`& .${tabClasses.selected}`]: {
              background: '#1B43321F',
            },
          }}
          slotProps={{
            indicator: {
              hidden: true,
            },
          }}
        >
          <Tab
            value={FilterPopoverTabs.Clients}
            component={ClientsTabButton}
            {...a11yProps(FilterPopoverTabs.Clients, BASE_TAB_ID)}
          />
          <Tab
            value={FilterPopoverTabs.Advertisers}
            component={AdvertisersTabButton}
            {...a11yProps(FilterPopoverTabs.Advertisers, BASE_TAB_ID)}
          />
          <Tab
            value={FilterPopoverTabs.Campaigns}
            component={CampaignsTabButton}
            {...a11yProps(FilterPopoverTabs.Campaigns, BASE_TAB_ID)}
          />
          {showEventFilter && (
            <Tab
              value={FilterPopoverTabs.Events}
              component={PixelsTabButton}
              {...a11yProps(FilterPopoverTabs.Events, BASE_TAB_ID)}
            />
          )}
        </Tabs>
        <TabPanel {...commonTabPanelProps} index={FilterPopoverTabs.Clients}>
          <TabSelect
            label='Client'
            loading={loadingClients}
            options={clients}
            value={pendingClients}
            onChange={updateClientsValue}
            onClose={handleClose}
            onSubmit={handleSubmit}
          />
        </TabPanel>
        <TabPanel {...commonTabPanelProps} index={FilterPopoverTabs.Advertisers}>
          <TabSelect
            label='Advertiser'
            loading={loadingClients}
            options={advertisers}
            value={pendingAdvertisers}
            onChange={updateAdvertisersValue}
            onClose={handleClose}
            onSubmit={handleSubmit}
          />
        </TabPanel>
        <TabPanel {...commonTabPanelProps} index={FilterPopoverTabs.Campaigns}>
          <TabSelect
            label='Campaign'
            loading={loadingCampaigns}
            options={campaigns}
            value={pendingCampaigns}
            onChange={updateCampaignsValue}
            onClose={handleClose}
            onSubmit={handleSubmit}
            grouped={groupedCampaigns}
          />
        </TabPanel>
        {showEventFilter && (
          <TabPanel {...commonTabPanelProps} index={FilterPopoverTabs.Events}>
            <TabSelect
              label='Pixels'
              loading={loadingEvents}
              options={events ?? []}
              value={pendingEvents}
              onChange={updateEventsValue}
              onClose={handleClose}
              onSubmit={handleSubmit}
            />
          </TabPanel>
        )}
      </TabFilterPopover>
      <FilterGroupChip items={filters.clients} type='Client' />
      <FilterGroupChip items={filters.advertisers} type='Advertiser' />
      <FilterGroupChip items={filters.campaigns} type='Campaign' />
      {showEventFilter && <FilterGroupChip items={filters.events} type='Pixel' />}
    </>
  );
};

export default FilterPopover;
